import {QueryResult} from '@apollo/client';
import {doesNotReject, rejects} from 'assert';
import React from 'react';

import {QueryResultType} from 'graphql-client/BenchlingQuery';
import notifications, {Response} from 'notifications';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import Modal from 'ui-library/modals/Modal';

import {DataPolicyCollaborationManagementContent} from './CollaborationManagementContent';
import * as PoliceableActions from './PoliceableActions';
import useCollaborationManagementState, {Policeable, Collaboration} from './useCollaborationManagementState';

export interface CollaborationManagementModalProps {
  onRequestSubmit?: (collaborations: Array<Collaboration>) => Promise<void>;
  onRequestClose: () => void;
  onUpdate?: () => void;
  policeable?: Policeable;
  policeableName: string;
  initialCollaborations: Array<Collaboration>;
  ancestorCollaborations: Array<Collaboration>;
  collaborations: Array<Collaboration>;
  setCollaborations: (collaborations: Array<Collaboration>) => void;
  refetch?: QueryResult['refetch'];
  submitButtonText?: string;
  saving?: boolean;
  renderAuditorColumn?: boolean;
}

export default function CollaborationManagementModal(props: CollaborationManagementModalProps): JSX.Element {
  const [collaborations, setCollaborations] = React.useState(props.collaborations);
  const [canSave, setCanSave] = React.useState<boolean>(true);
  const canManageCollaborators =
    props.policeable == null
      ? true
      : (props.policeable.permissions.canManageCollaborators ?? props.policeable.permissions.admin);
  const readOnly = props.saving === true ? props.saving : !canManageCollaborators;

  const _handleLeave = (collaboration: Collaboration): Promise<void> =>
    PoliceableActions.removeCollaborator(props.policeable!.id, collaboration.party.id)
      .then(() => {
        props.onRequestClose();
        props.onUpdate?.();
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .catch((reason: any) => {
        if (reason.isCanceled) {
          return doesNotReject(reason);
        }
        return rejects(reason);
      })
      .finally((): void => {
        props.refetch?.();
      });

  return (
    <Modal
      title={`Manage Access to ${props.policeableName}`}
      onRequestSubmit={
        canManageCollaborators
          ? async () => {
              // Submit changes by setting the local state to the shared state defined by
              // `useCollaborationManagementState`.
              props.setCollaborations(collaborations);
              if (props.onRequestSubmit != null) {
                await props.onRequestSubmit(collaborations);
              }
            }
          : undefined
      }
      onRequestClose={() => {
        props.onRequestClose();
      }}
      closeable={false}
      buttons={[
        {
          cancel: true,
          text: canManageCollaborators ? 'Discard changes' : 'Close',
          disabled: props.saving,
          dataTestAttrs: {
            component: 'CollaborationManagementModal',
            element: 'cancelButton',
          },
        },
        ...(canManageCollaborators
          ? [
              {
                submit: true,
                text: props.submitButtonText ?? 'Done',
                disabled: readOnly || !canSave,
                dataTestAttrs: {
                  component: 'CollaborationManagementModal',
                  element: 'submitButton',
                },
              },
            ]
          : []),
      ]}
      dataTestAttrs={{component: 'CollaborationManagementModal', element: 'modal'}}
    >
      <DataPolicyCollaborationManagementContent
        policeable={props.policeable}
        collaborations={collaborations}
        ancestorCollaborations={props.ancestorCollaborations}
        setCollaborations={setCollaborations}
        renderAuditorColumn={props.renderAuditorColumn}
        onLeave={
          ['ProjectNode', 'RegistryNode', 'FolderNode'].includes(props.policeable?.__typename ?? '')
            ? _handleLeave
            : undefined
        }
        isReadOnly={readOnly}
        modalRender={true}
        setCanSave={setCanSave}
      />
    </Modal>
  );
}

interface SavingCollaborationManagementModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (collaborations: Array<Collaboration>) => Promise<void>;
  policeableId: string;
  renderAuditorColumn?: boolean;
}

export function SavingCollaborationManagementModal(
  props: SavingCollaborationManagementModalProps
): JSX.Element {
  const [state, actions] = useCollaborationManagementState(props.policeableId);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (state.queryResultType === QueryResultType.PENDING) {
      LoadingScreen.show();
    } else {
      LoadingScreen.hide();
    }
  }, [state.queryResultType]);

  if (state.queryResultType === QueryResultType.ERROR) {
    notifications.error('Could not load collaborators; try reloading the page.');
    return <></>;
  } else if (state.queryResultType !== QueryResultType.SUCCESS) {
    return <></>;
  }
  return (
    <CollaborationManagementModal
      {...state}
      {...actions}
      renderAuditorColumn={props.renderAuditorColumn ?? false}
      submitButtonText="Save"
      saving={saving}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={async (collaborations: Array<Collaboration>) => {
        setSaving(true);
        await props.onRequestSubmit(collaborations).catch((res: Response) => {
          notifications.notifyError(res);
          setSaving(false);
        });
        await actions.refetch?.();
        LoadingScreen.hide();
      }}
      policeableName={state.policeable!.name}
    />
  );
}
